import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { distinctUntilKeyChanged, map, Observable, switchMap, take } from 'rxjs';

import { AccountAccessStatus, AccountMember, AccountMemberRole, InvitationStatus, UserService } from '@celum/authentication';
import { CelumPropertiesProvider } from '@celum/core/configuration';
import { isTruthy } from '@celum/core/rxjs';
import { Result } from '@celum/ng2base/entity';

import { IAccountContinuationPaginationResult } from './account-user.service';

// Account Member sortable fields.
type SortableFields = 'displayName' | 'firstName';
type SortParam = `${SortableFields},${'asc' | 'desc'}`;

export type AccountMemberFindQueryParams = {
  ids?: string[];
  searchValue?: string;
  invitationStatuses?: InvitationStatus[];
  membershipStatuses?: AccountAccessStatus[];
  effectiveRoleIn?: AccountMemberRole[];
  limit?: number;
  sort?: SortParam;
  continuationToken?: string;
};

type AccountServiceCallResult = { entities: any[]; filterCount?: number; totalCount: number; continuationToken: string };

@Injectable({ providedIn: 'root' })
export class AccountMemberService {
  private static readonly DEFAULT_MEMBER_SEARCH_SIZE = 1000;
  private readonly userService = inject(UserService);
  private readonly http = inject(HttpClient);

  public findMembers(options: AccountMemberFindQueryParams): Observable<Result<AccountMember, IAccountContinuationPaginationResult>> {
    return this.userService.accountAccessForCurrentTenant$.pipe(
      isTruthy(),
      take(1),
      distinctUntilKeyChanged('accountId'),
      map(tenant => tenant?.accountId),
      switchMap(accountId =>
        this.http.post<AccountServiceCallResult>(
          `${CelumPropertiesProvider.properties.authentication.saccUrl}/accounts/${accountId}/members/search`,
          {
            idOrOidIn: options.ids,
            nameOrEmailContains: options.searchValue,
            invitationStatusIn: options.invitationStatuses,
            statusIn: options.membershipStatuses || [AccountAccessStatus.ACTIVE],
            effectiveRoleIn: options.effectiveRoleIn
          },
          {
            params: {
              size: (options?.limit ?? options.ids?.length ?? AccountMemberService.DEFAULT_MEMBER_SEARCH_SIZE).toString(),
              ...(options?.sort && { sort: options.sort }),
              ...(options?.continuationToken && { continuationToken: options.continuationToken })
            }
          }
        )
      ),
      map(result => AccountMemberService.mapMemberResult(result))
    );
  }

  private static mapMemberResult(result: AccountServiceCallResult): Result<AccountMember, IAccountContinuationPaginationResult> {
    return {
      entities: result.entities,
      paging: {
        hasBottom: !!result.continuationToken,
        hasTop: true,
        filterCount: result.filterCount,
        totalElementCount: result.totalCount,
        continuationToken: result.continuationToken
      }
    };
  }
}
